import { jsPDF } from "jspdf";

import CairoBold from "../../../assets/fonts/Cairo-Bold.ttf";
import CairoRegular from "../../../assets/fonts/Cairo-Regular.ttf";

const COLORS = {
  black: "000000",
  blue: "2D9CDB",
  divider: "4C4C4C",
  muted: "A9B1C1",
  white: "FFFFFF",
  placeholder: "171717",
};

// LAYOUT_WIDE slide size in inches (matches the previous pptx export).
const PAGE_WIDTH = 13.33;
const PAGE_HEIGHT = 7.5;

const hexToRgb = (hex) => [
  parseInt(hex.slice(0, 2), 16),
  parseInt(hex.slice(2, 4), 16),
  parseInt(hex.slice(4, 6), 16),
];

const safeText = (value, fallback = "-") => {
  if (value === 0) return "0";
  if (value == null) return fallback;
  const text = String(value).trim();
  return text || fallback;
};

const readLocalizedName = (item, lang) =>
  safeText(
    lang === "ar"
      ? item?.full_name_ar || item?.name || item?.full_name_en
      : item?.full_name_en || item?.name || item?.full_name_ar,
  );

const readNestedName = (value) => {
  if (!value) return "-";
  if (typeof value === "string") return safeText(value);
  return safeText(value.name || value.name_en || value.name_ar);
};

const readLanguages = (languages = []) =>
  languages
    .map((item) => readNestedName(item?.language || item))
    .filter((item) => item && item !== "-")
    .join(", ") || "-";

const sanitizeFileName = (name) =>
  safeText(name, "profile").replace(/[\\/:*?"<>|]/g, "-");

// jsPDF only embeds raster PNG/JPEG. Profile photos vary in format and the
// ProLine logo is an SVG, none of which jsPDF can decode directly — so
// rasterize every source to a PNG data URL through an offscreen canvas first.
const RASTER_SCALE = 3;
const toPngDataUrl = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      const width = (img.naturalWidth || 600) * RASTER_SCALE;
      const height = (img.naturalHeight || 200) * RASTER_SCALE;
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

// jsPDF's built-in fonts are WinAnsi-encoded and carry no Arabic glyphs, so an
// Arabic export has to embed a real Unicode TTF. Cairo covers Arabic + Latin;
// jsPDF then handles the letter shaping and bidi reordering on its own.
const DEFAULT_FONT = "helvetica";
const ARABIC_FONT = "Cairo";
let arabicFontPromise = null;

const toBase64 = async (url) => {
  const bytes = new Uint8Array(await (await fetch(url)).arrayBuffer());
  const CHUNK = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return window.btoa(binary);
};

const loadArabicFont = () => {
  if (!arabicFontPromise) {
    arabicFontPromise = Promise.all([
      toBase64(CairoRegular),
      toBase64(CairoBold),
    ]).catch((error) => {
      arabicFontPromise = null;
      throw error;
    });
  }
  return arabicFontPromise;
};

// jsPDF shapes Arabic by rewriting each letter to an Arabic Presentation
// Forms-B codepoint (U+FE70–U+FEFC). Cairo is an OpenType font that expects a
// real shaper, so it only maps part of that legacy block: the initial, medial
// and final forms are there, but every *isolated* form is not. Those letters
// resolve to no glyph and silently drop out of the PDF — e.g. the alef in
// "المدينة" (shaped to U+FE8D). Folding the unmapped forms back to their base
// letter restores them, and the base letter's default Cairo glyph is the
// isolated shape, so the output is identical to what the shaper intended.
const UNMAPPED_ISOLATED_FORMS = new RegExp(
  "[\\uFE70-\\uFE7F\\uFE80\\uFE81\\uFE83\\uFE85\\uFE87\\uFE89\\uFE8D\\uFE8F" +
    "\\uFE93\\uFE95\\uFE99\\uFE9D\\uFEA1\\uFEA5\\uFEA9\\uFEAB\\uFEAD\\uFEAF" +
    "\\uFEB1\\uFEB5\\uFEB9\\uFEBD\\uFEC1\\uFEC5\\uFEC9\\uFECD\\uFED1\\uFED5" +
    "\\uFED9\\uFEDD\\uFEE1\\uFEE5\\uFEE9\\uFEED\\uFEEF\\uFEF1]",
  "g",
);

const foldToBaseLetters = (value) =>
  typeof value === "string"
    ? value.replace(UNMAPPED_ISOLATED_FORMS, (char) => char.normalize("NFKC"))
    : value;

// Runs on preProcessText, which fires after jsPDF's own Arabic shaper, so the
// text arriving here is already in presentation forms. jsPDF normalizes the
// payload to an array of lines, each line being either a string or a
// [string, x, y] tuple.
const restoreUnmappedGlyphs = (payload) => {
  if (!Array.isArray(payload.text)) {
    payload.text = foldToBaseLetters(payload.text);
    return;
  }
  payload.text = payload.text.map((line) =>
    Array.isArray(line)
      ? [foldToBaseLetters(line[0]), line[1], line[2]]
      : foldToBaseLetters(line),
  );
};

// Returns the font family to draw with — falls back to the built-in font if
// the TTFs can't be fetched, so the export still produces a file.
const registerArabicFont = async (doc) => {
  try {
    const [regular, bold] = await loadArabicFont();
    doc.addFileToVFS("Cairo-Regular.ttf", regular);
    doc.addFont("Cairo-Regular.ttf", ARABIC_FONT, "normal");
    doc.addFileToVFS("Cairo-Bold.ttf", bold);
    doc.addFont("Cairo-Bold.ttf", ARABIC_FONT, "bold");
    doc.internal.events.subscribe("preProcessText", restoreUnmappedGlyphs);
    return ARABIC_FONT;
  } catch (error) {
    return DEFAULT_FONT;
  }
};

// jsPDF font size is in points; box coords are in inches (72pt per inch).
const setText = (doc, { size, bold, color }) => {
  doc.setFont(doc.textFontFamily || DEFAULT_FONT, bold ? "bold" : "normal");
  doc.setFontSize(size);
  doc.setTextColor(...hexToRgb(color));
};

// Shrink font size until the text fits within maxWidth (inches), mirroring
// pptx `fit: "shrink"`.
const fittedSize = (doc, text, size, maxWidth) => {
  let current = size;
  doc.setFontSize(current);
  while (current > 6 && doc.getTextWidth(text) > maxWidth) {
    current -= 0.5;
    doc.setFontSize(current);
  }
  return current;
};

const drawText = (doc, text, x, y, opts = {}) => {
  const { size = 10, bold = false, color = COLORS.white, w } = opts;
  setText(doc, { size, bold, color });
  const value = safeText(text);
  if (w) doc.setFontSize(fittedSize(doc, value, size, w));
  doc.text(value, x, y, { baseline: "top" });
};

const drawRect = (doc, x, y, w, h, color) => {
  doc.setFillColor(...hexToRgb(color));
  doc.rect(x, y, w, h, "F");
};

const addLabelValue = (doc, label, value, x, y) => {
  drawText(doc, `${safeText(label)}:`, x, y, {
    size: 9,
    bold: true,
    color: COLORS.muted,
  });
  drawText(doc, value, x + 1.7, y, { size: 10, color: COLORS.white, w: 3.4 });
};

const addSectionTitle = (doc, title, x, y) => {
  drawText(doc, title, x, y, { size: 12, bold: true, color: COLORS.white });
};

const addProfileImage = (doc, imageData) => {
  if (imageData) {
    doc.addImage(imageData, "PNG", 0.7, 1.65, 2.3, 3.35);
    return;
  }
  drawRect(doc, 0.7, 1.65, 2.3, 3.35, COLORS.placeholder);
};

export const downloadProfile = async (
  applicant,
  applicants,
  lang,
  proLineLogo,
  userBase,
) => {
  const isListingPage = Array.isArray(applicant);
  const applicantList = Array.isArray(applicant) ? applicant : [applicant];

  // Rasterize all images up front (SVG logo + profile photos) so the PDF
  // build below stays synchronous.
  const logoPng = await toPngDataUrl(proLineLogo);
  const profileImages = await Promise.all(
    applicantList.map((_, index) =>
      toPngDataUrl(isListingPage ? userBase?.[index]?.imageBase64 : userBase),
    ),
  );

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "in",
    format: [PAGE_WIDTH, PAGE_HEIGHT],
  });
  doc.textFontFamily =
    lang === "ar" ? await registerArabicFont(doc) : DEFAULT_FONT;
  doc.setProperties({
    author: "Yoram",
    subject: applicants.applicantExportDetails.applicantProfile,
    title: applicants.applicantExportDetails.applicantProfile,
  });

  applicantList.forEach((info, index) => {
    if (index > 0) doc.addPage([PAGE_WIDTH, PAGE_HEIGHT], "landscape");

    const profileImage = profileImages[index];
    const displayName = readLocalizedName(info, lang);
    const roleName = readNestedName(info?.role);
    const fieldOfStudy =
      readNestedName(info?.field_of_study) !== "-"
        ? readNestedName(info?.field_of_study)
        : info?.field_of_study_name;

    drawRect(doc, 0, 0, PAGE_WIDTH, PAGE_HEIGHT, COLORS.black);

    if (logoPng) {
      doc.addImage(logoPng, "PNG", 0.65, 0.35, 1.45, 0.48);
    }

    drawText(doc, applicants.listApplicants.getKnowTeam, 2.35, 0.45, {
      size: 18,
      bold: true,
      color: COLORS.white,
      w: 5.8,
    });

    drawRect(doc, 2.35, 0.9, 8.9, 0.04, COLORS.divider);

    addProfileImage(doc, profileImage);

    drawText(doc, displayName, 3.45, 1.55, {
      size: 22,
      bold: true,
      color: COLORS.white,
      w: 5.6,
    });

    drawRect(doc, 3.45, 2.08, 2.8, 0.36, COLORS.blue);
    drawText(doc, roleName, 3.58, 2.15, {
      size: 11,
      bold: true,
      color: COLORS.white,
      w: 2.55,
    });

    addSectionTitle(doc, applicants.applicantExportDetails.basicInfo, 3.45, 2.8);
    addLabelValue(doc, applicants.applicantExportDetails.id, info?.id, 3.45, 3.22);
    addLabelValue(
      doc,
      applicants.applicantExportDetails.nationality,
      readNestedName(info?.nationality),
      3.45,
      3.62,
    );
    addLabelValue(
      doc,
      applicants.applicantExportDetails.city,
      readNestedName(info?.city),
      3.45,
      4.02,
    );

    addSectionTitle(doc, applicants.applicantExportDetails.education, 7.4, 2.8);
    addLabelValue(
      doc,
      applicants.applicantExportDetails.educationLevel,
      readNestedName(info?.educational_level),
      7.4,
      3.22,
    );
    addLabelValue(
      doc,
      applicants.applicantExportDetails.filedsOfStudy,
      fieldOfStudy,
      7.4,
      3.62,
    );
    addLabelValue(
      doc,
      applicants.applicantExportDetails.language,
      readLanguages(info?.user_languages),
      7.4,
      4.02,
    );

    addSectionTitle(doc, applicants.applicantExportDetails.bankInfo, 3.45, 4.82);
    addLabelValue(
      doc,
      applicants.applicantExportDetails.bankName,
      readNestedName(info?.bank),
      3.45,
      5.24,
    );
    addLabelValue(
      doc,
      applicants.applicantExportDetails.bankIban,
      info?.iban,
      3.45,
      5.64,
    );
    addLabelValue(
      doc,
      applicants.applicantExportDetails.bankCardHolder,
      info?.card_holder_name,
      3.45,
      6.04,
    );
  });

  const baseName = isListingPage
    ? applicants.applicantExportDetails.applicantProfile
    : `${sanitizeFileName(readLocalizedName(applicant, lang))}-${applicants.applicantExportDetails.profile}`;
  const fileName = baseName.toLowerCase().endsWith(".pdf")
    ? baseName
    : `${baseName}.pdf`;

  doc.save(fileName);
};
